import type { PostgrestError } from '@supabase/supabase-js';

import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import {
  type AttachOwnedGoogleCampaignInput,
  type AttachOwnedGoogleCampaignResponse,
  AttachOwnedGoogleCampaignResponseSchema,
  type AttachOwnedMetaCreativeInput,
  type AttachOwnedMetaCreativeResponse,
  AttachOwnedMetaCreativeResponseSchema,
  type BriefEvidenceDecision,
  type BriefEvidenceReadModel,
  type BriefEvidenceSource,
  type BriefPriorLearningInput,
  EvidenceRelationshipSchema,
} from '@/lib/briefs/evidence-contract';
import { isConflictCode } from '@/lib/errors/conflict-code';
import { type BriefMeasurement, parseBriefMeasurement } from '@/lib/paid-media/measurement-plan';
import { invokeEdgeFunction } from '@/utils/supabaseEdgeFunctions';

export interface UpsertBriefDecisionInput {
  briefId: string;
  /** The version the caller read. 0 means no decision exists yet. */
  expectedVersion: number;
  audience: string | null;
  offer: string | null;
  hypothesis: string;
  testVariable: string | null;
  channelRole: BriefEvidenceDecision['channelRole'];
  successMeasure: string | null;
  evidenceLimitations: string[];
  /** Omit to keep the stored measurement; the RPC never erases it. */
  measurement?: BriefMeasurement | null;
}

type BriefDecisionRow = Database['public']['Tables']['brief_evidence_decisions']['Row'];

type UpsertBriefDecisionRpc = (
  fn: 'upsert_brief_decision',
  args: {
    p_brief_id: string;
    p_expected_version: number;
    p_audience: string | null;
    p_offer: string | null;
    p_hypothesis: string;
    p_test_variable: string | null;
    p_channel_role: string | null;
    p_success_measure: string | null;
    p_evidence_limitations: string[];
    p_measurement: BriefMeasurement | null;
  },
) => PromiseLike<{ data: BriefDecisionRow | null; error: PostgrestError | null }>;

function friendlyDecisionError(error: PostgrestError): Error {
  if (isConflictCode(error.code)) return new Error('This decision changed in another session.');
  if (error.code === '42501') return new Error('You do not have access to this Brief.');
  return new Error(error.message);
}

export class BriefEvidenceService {
  async filterBriefIdsForConnection(
    connectionId: string,
    briefIds: string[],
  ): Promise<Set<string>> {
    if (briefIds.length === 0) return new Set();
    const { data: connection, error: connectionError } = await supabase
      .from('user_connections')
      .select('brand_kit_id')
      .eq('id', connectionId)
      .maybeSingle();
    if (connectionError) throw new Error(connectionError.message);
    if (!connection?.brand_kit_id) return new Set();
    const { data: briefs, error: briefsError } = await supabase
      .from('projects')
      .select('id')
      .in('id', briefIds)
      .eq('kind', 'brief')
      .eq('brand_kit_id', connection.brand_kit_id);
    if (briefsError) throw new Error(briefsError.message);
    return new Set((briefs ?? []).map((brief) => brief.id));
  }

  async listDecisionChannels(
    briefIds: string[],
  ): Promise<Map<string, BriefEvidenceDecision['channelRole']>> {
    if (briefIds.length === 0) return new Map();
    const { data, error } = await supabase
      .from('brief_evidence_decisions')
      .select('brief_id, channel_role')
      .in('brief_id', briefIds);
    if (error) throw new Error(error.message);
    return new Map(
      (data ?? []).map((row) => [
        row.brief_id,
        row.channel_role as BriefEvidenceDecision['channelRole'],
      ]),
    );
  }

  async list(briefId: string): Promise<BriefEvidenceReadModel> {
    const [linksResult, decisionResult, learningInputsResult] = await Promise.all([
      supabase
        .from('brief_evidence_links')
        .select('id, relationship, evidence_snapshot, linked_at')
        .eq('brief_id', briefId)
        .is('unlinked_at', null)
        .order('linked_at', { ascending: false }),
      supabase
        .from('brief_evidence_decisions')
        .select(
          'audience, offer, hypothesis, test_variable, channel_role, success_measure, measurement, evidence_limitations, version, updated_at, updated_by',
        )
        .eq('brief_id', briefId)
        .maybeSingle(),
      supabase
        .from('brief_learning_inputs')
        .select('id, learning_id, decision_version, role, learning_snapshot, attached_at')
        .eq('brief_id', briefId)
        .order('attached_at', { ascending: false }),
    ]);

    if (linksResult.error) throw new Error(linksResult.error.message);
    if (decisionResult.error) throw new Error(decisionResult.error.message);
    if (learningInputsResult.error) throw new Error(learningInputsResult.error.message);

    const sources: BriefEvidenceSource[] = (linksResult.data ?? []).map((row) => ({
      id: row.id,
      relationship: EvidenceRelationshipSchema.parse(row.relationship),
      snapshot:
        row.evidence_snapshot &&
        typeof row.evidence_snapshot === 'object' &&
        !Array.isArray(row.evidence_snapshot)
          ? (row.evidence_snapshot as Record<string, unknown>)
          : {},
      linkedAt: row.linked_at,
    }));

    const row = decisionResult.data;
    const decision: BriefEvidenceDecision | null = row
      ? {
          audience: row.audience,
          offer: row.offer,
          hypothesis: row.hypothesis,
          testVariable: row.test_variable,
          channelRole: row.channel_role as BriefEvidenceDecision['channelRole'],
          successMeasure: row.success_measure,
          measurement: parseBriefMeasurement(row.measurement),
          evidenceLimitations: row.evidence_limitations,
          version: row.version,
          updatedAt: row.updated_at,
          updatedBy: row.updated_by,
        }
      : null;

    const priorLearnings: BriefPriorLearningInput[] = (learningInputsResult.data ?? []).map(
      (input) => ({
        id: input.id,
        learningId: input.learning_id,
        decisionVersion: input.decision_version,
        role: input.role as BriefPriorLearningInput['role'],
        snapshot:
          input.learning_snapshot &&
          typeof input.learning_snapshot === 'object' &&
          !Array.isArray(input.learning_snapshot)
            ? (input.learning_snapshot as Record<string, unknown>)
            : {},
        attachedAt: input.attached_at,
      }),
    );

    return { sources, decision, priorLearnings };
  }

  /**
   * The decision's only front door.
   *
   * Until this existed a decision could ONLY be created as a side effect of
   * attaching evidence, which is why 2 of 22 Briefs had one. Writes stay
   * RPC-only — `brief_evidence_decisions` carries a SELECT policy and nothing
   * else — so this calls `upsert_brief_decision` rather than writing the table.
   *
   * `expectedVersion` is the version the caller read; 0 means "no decision
   * yet", which makes create and update one call. A stale version raises
   * 40001 instead of silently overwriting a colleague's edit.
   */
  async upsertDecision(input: UpsertBriefDecisionInput): Promise<BriefEvidenceDecision> {
    const rpc = supabase.rpc.bind(supabase) as unknown as UpsertBriefDecisionRpc;
    const { data, error } = await rpc('upsert_brief_decision', {
      p_brief_id: input.briefId,
      p_expected_version: input.expectedVersion,
      p_audience: input.audience,
      p_offer: input.offer,
      p_hypothesis: input.hypothesis,
      p_test_variable: input.testVariable,
      p_channel_role: input.channelRole,
      p_success_measure: input.successMeasure,
      p_evidence_limitations: input.evidenceLimitations,
      p_measurement: input.measurement ?? null,
    });
    if (error) throw friendlyDecisionError(error);
    if (!data) throw new Error('Saving the decision returned no result.');
    return {
      audience: data.audience,
      offer: data.offer,
      hypothesis: data.hypothesis,
      testVariable: data.test_variable,
      channelRole: data.channel_role as BriefEvidenceDecision['channelRole'],
      successMeasure: data.success_measure,
      measurement: parseBriefMeasurement(data.measurement),
      evidenceLimitations: data.evidence_limitations,
      version: data.version,
      updatedAt: data.updated_at,
      updatedBy: data.updated_by,
    };
  }

  async attachOwnedMetaCreative(
    input: AttachOwnedMetaCreativeInput,
  ): Promise<AttachOwnedMetaCreativeResponse> {
    const response = await invokeEdgeFunction<unknown>('attach-owned-meta-creative-to-brief', {
      body: input,
    });
    const parsed = AttachOwnedMetaCreativeResponseSchema.safeParse(response);
    if (!parsed.success) {
      throw new Error('Owned Meta creative evidence returned an invalid response.');
    }
    return parsed.data;
  }

  async attachOwnedGoogleCampaign(
    input: AttachOwnedGoogleCampaignInput,
  ): Promise<AttachOwnedGoogleCampaignResponse> {
    const session = await supabase.auth.getSession();
    const token = session.data.session?.access_token;
    if (!token) throw new Error('Sign in again before attaching Google Ads evidence.');
    const response = await fetch('/api/briefs/evidence/owned-google-campaign', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(input),
    });
    const body = (await response.json().catch(() => null)) as unknown;
    if (!response.ok) {
      const error =
        body && typeof body === 'object' && 'error' in body
          ? String((body as { error: unknown }).error)
          : 'Google campaign evidence could not be attached.';
      throw new Error(error);
    }
    const parsed = AttachOwnedGoogleCampaignResponseSchema.safeParse(body);
    if (!parsed.success) throw new Error('Google campaign evidence returned an invalid response.');
    return parsed.data;
  }
}

export const briefEvidenceService = new BriefEvidenceService();
